import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { hasPerm } from "../lib/permissions";

export default function Configuration() {
  const { user } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [newDept, setNewDept] = useState("");
  const [loading, setLoading] = useState(true);

  const canEdit = hasPerm(user, "configuration", "edit");

  const refresh = async () => {
    try {
      const { data } = await api.get("/config/departments");
      setDepartments(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const add = async () => {
    if (!newDept.trim()) return;
    try {
      await api.post("/config/departments", { name: newDept.trim() });
      setNewDept("");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (name) => {
    if (!window.confirm(`Remove department "${name}"?`)) return;
    try {
      await api.delete(`/config/departments/${encodeURIComponent(name)}`);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="configuration-page" className="space-y-6">
      <header>
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#00D4FF] mb-1">Configuration</div>
        <h1 className="font-heading text-3xl font-bold">Departments</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Manage the list of departments shown when assigning users.
        </p>
      </header>

      <div className="bg-[#12141D] border border-border p-5 max-w-xl">
        {canEdit && (
          <div className="flex gap-2 mb-4">
            <input
              data-testid="new-department-input"
              value={newDept}
              onChange={(e) => setNewDept(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="Add a department (e.g. Logistics)"
              className="flex-1 bg-[#0A0B10] border border-border px-3 py-2 text-sm focus:outline-none focus:border-[#00D4FF]"
            />
            <button
              data-testid="add-department-btn"
              onClick={add}
              className="flex items-center gap-1.5 bg-[#00D4FF] text-[#0A0B10] px-3 py-2 text-xs font-semibold tracking-wider uppercase hover:bg-[#00B8D4]"
            >
              <Plus size={14} /> Add
            </button>
          </div>
        )}
        {loading ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : (
          <ul className="divide-y divide-border/60">
            {departments.map((d) => (
              <li key={d} className="flex items-center justify-between py-2.5">
                <span className="text-sm">{d}</span>
                {canEdit && (
                  <button
                    data-testid={`delete-dept-${d}`}
                    onClick={() => del(d)}
                    className="p-1.5 text-muted-foreground hover:text-red-400"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </li>
            ))}
            {departments.length === 0 && (
              <li className="text-sm text-muted-foreground py-4 text-center">No departments yet.</li>
            )}
          </ul>
        )}
      </div>
    </div>
  );
}
